import AppPagination from "@/component/common/AppPagination.tsx";
import ConfirmButton from "@/component/common/button/ConfirmButton.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import UserRoleTag from "@/component/tag/UserRoleTag.tsx";
import { ROLE_ENUM } from "@/constant/constants.ts";
import { StringHelper } from "@/constant/helper.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { UserService } from "@/services/user/UserService.ts";
import { toast } from "@/utils/toast.tsx";
import { PlusOutlined } from "@ant-design/icons";
import {
    Button,
    Flex,
    Space,
    Table,
    TablePaginationConfig,
    TableProps,
} from "antd";
import Search from "antd/es/input/Search";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const generateHeaderTable = (
  t: TFunction,
  research: () => void,
): TableProps<UserDetail>["columns"] => {
  return [
    {
      title: t("field.no"),
      dataIndex: "no",
      key: "no",
      align: "center",
      render: (_, _record, index) => <span>{index + 1}</span>,
    },
    {
      title: t("field.firstName"),
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: t("field.lastName"),
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: t("field.username"),
      dataIndex: "username",
      key: "username",
    },
    {
      title: t("field.role"),
      key: "role",
      dataIndex: "role",
      render: (_, { role }) => <UserRoleTag status={role} />,
    },
    {
      title: t("field.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("field.mobile"),
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: t("field.action"),
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <EditButton record={record} />
          <DeleteButton record={record} research={research} />
        </Space>
      ),
    },
  ];
};

const EditButton = ({ record }: { record: UserDetail }) => {
  const { t } = useTranslation();
  const navigation = useNavigate();

  return (
    <Button
      color={"primary"}
      variant={"filled"}
      onClick={() => {
        const params = new URLSearchParams();
        params.append("userId", String(record?.userId));
        navigation(`${MANAGER_PATH.USER.EDIT}?${params.toString()}`);
      }}
    >
      {t("button.edit")}
    </Button>
  );
};

const DeleteButton = ({
  record,
  research,
}: {
  record: UserDetail;
  research: () => void;
}) => {
  const { t } = useTranslation();

  const handleDeleteUser = async (userId?: number | null) => {
    if (userId) {
      const res: SuccessResponse | null | undefined =
        await UserService.delete(userId);
      if (res) {
        research();
        toast.success(res?.message || "Success");
      }
    }
  };

  return (
    <ConfirmButton
      buttonText={t("button.delete")}
      buttonProps={{
        color: "danger",
        variant: "filled",
        disabled: record.role === ROLE_ENUM.ADMIN,
      }}
      confirmMessage={t("message.confirmDeleteUser")}
      handleOk={() => handleDeleteUser(record?.userId)}
    />
  );
};

const UserManagement = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();

  const isFirstRun = useRef<boolean>(true);
  const [dataTable, setDataTable] = useState<UserDetail[]>([]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [searchRequest, setSearchRequest] = useState<SearchUserListRequest>({
    keyword: "",
    size: 10,
    page: 0,
    sortBy: ["user_id"],
    sortDesc: [false],
  });

  const research = async () => {
    const res: SearchUserResponse | null | undefined =
      await UserService.search(searchRequest);
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items || []);
  };
  const columns: TableProps<UserDetail>["columns"] = generateHeaderTable(
    t,
    research,
  );

  const onChangeKeySearch = (value: string) => {
    setSearchRequest((prevState) => ({ ...prevState, keyword: value }));
  };

  const onChangePageNum = (page: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  const onChangeTable = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserDetail> | SorterResult<UserDetail>[],
  ) => {
    const sortValue = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!sortValue.column) {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortBy: ["user_id"],
        sortDesc: [false],
      }));
    } else {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortDesc: [sortValue.order === "descend"],
        sortBy: [StringHelper.toSnakeCase(String(sortValue.columnKey))],
      }));
    }
  };

  const onClickAddNewUser = () => {
    navigation(MANAGER_PATH.USER.ADD);
  };

  useEffect(() => {
    async function searchUserList() {
      const res: SearchUserResponse | null | undefined =
        await UserService.search(searchRequest);
      if (!res?.items || res?.items?.length === 0) {
        setTotalRecord(0);
        setDataTable([]);
        if (searchRequest.page > 0) {
          onChangePageNum(1);
          return;
        }
        if (!isFirstRun.current) {
          toast.warning(t("message.noResultsFound"));
        }
        return;
      }
      setTotalRecord(res?.total || 0);
      setDataTable(res?.items);
    }

    searchUserList().finally(() => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
      }
    });
  }, [searchRequest]);

  return (
    <>
      <ManageContentLayout
        headerFunction={
          <>
            <Button
              icon={<PlusOutlined />}
              type={"primary"}
              size={"large"}
              onClick={() => onClickAddNewUser()}
            >
              {t("button.addUser")}
            </Button>
          </>
        }
      >
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          {/* Search block */}
          <Flex justify={"flex-start"}>
            <Search
              style={{ width: 555 }}
              placeholder={t("placeholder.search")}
              onSearch={onChangeKeySearch}
              enterButton
              size={"large"}
            />
          </Flex>

          {/* Table function block */}
          <div className={"mt-5"}>
            <Table
              rowKey={"userId"}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
              onChange={onChangeTable}
            />
            <AppPagination
              className={"mt-3"}
              pageSize={searchRequest.size}
              onChangePageSize={onChangePageSize}
              onChangePage={onChangePageNum}
              currentPage={searchRequest.page}
              total={totalRecord}
            />
          </div>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default UserManagement;
